import type { ReactNode } from 'react';
import React, { createContext, useContext } from 'react';

import type { ItemQuantities } from '../helpers/assignment-helpers';
import type { OrderItem } from '../types';
import type { PackageType } from '@shipstation/client-common/types/seller-data';
import type { Dimensions, Weight } from '../../multi-package-modal/package-set';

type MultiPackageModalContextValue = {
  hasInsuranceProviderSelected: boolean;
  shouldRequirePackageDescription: boolean;
  customPackages: PackageType[];
  items: OrderItem[];
  itemQuantities: ItemQuantities;
  changePackageDimensions: (dimensions: Dimensions, packageIndex: number) => void;
  changeWeight: (weight: Weight, packageIndex: number) => void;
  changePackageContentDescription: (description: string, packageIndex: number) => void;
  changeInsuranceValue: (amount: number, currencyCode: string, packageIndex: number) => void;
  applyCustomPackageToPackage: (customPackage: PackageType, packageIndex: number) => void;
  unassignItemFromPackage: (itemId: string, chunkId: string) => void;
  updateChunkQuantity: (itemId: string, chunkId: string, newQuantity: number) => void;
};

const MultiPackageModalContext = createContext<MultiPackageModalContextValue | null>(null);

export const useMultiPackageModalContext = () => {
  const context = useContext(MultiPackageModalContext);
  if (!context) {
    throw new Error('useMultiPackageModalContext must be used within MultiPackageModalProvider');
  }
  return context;
};

type MultiPackageModalProviderProps = {
  children: ReactNode;
  value: MultiPackageModalContextValue;
};

export const MultiPackageModalProvider = ({ children, value }: MultiPackageModalProviderProps) => {
  return <MultiPackageModalContext.Provider value={value}>{children}</MultiPackageModalContext.Provider>;
};
